import React, { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, ConfigProvider, Dropdown, InputNumber, Segmented, Select, Spin, Tooltip } from "antd";
import { CloseOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { track } from "statsig";
import { STATSIG } from "statsig/events";
import * as com from "../../../../Common.js";
import RateQuoteNewResultTable from "../../RateQuoteNewResultTable";
import usePpeQuotes from "./usePpeQuotes";
import { toggledSet } from "./PpeResults";
import { applyAlternative, fetchAlternatives, shownAlternatives } from "./alternatives";
import { TargetBasisHint, TargetBasisPicker } from "../common/targetBasis";
import { TARGET_BASIS_COST, TARGET_BASIS_PRICE } from "../../pricingRequest";
import {
  barLoanAmount,
  buildProducts,
  countBy,
  filterBarBody,
  incomeEntered,
  initialBarTarget,
  lenderOptions,
  loanAmountOf,
  mergeIneligible,
  startingTargetRate,
  termGroupsFor,
  termLabel,
  termsOfSearch,
  visibleProducts,
  zeroSentence,
  quotePhase,
} from "./resultModel";
import "./index.css";

const SECOND_LIEN_PROGRAMS = ["heloc", "secondMortgage"];
const numberOr = (value, fallback) => (Number.isFinite(Number(value)) && value !== null && value !== "" ? Number(value) : fallback);
const round2 = (value) => Math.round(value * 100) / 100;


const suffix_svg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path fillRule="evenodd" clipRule="evenodd" d="M14.8425 8.7999L13.9587 7.91602L10.129 11.7457L6.30088 7.91755L5.41699 8.80143L9.24512 12.6296L9.24461 12.6301L10.1285 13.5139L10.129 13.5134L10.131 13.5155L11.0149 12.6316L11.0129 12.6296L14.8425 8.7999Z" fill="#6E6E70" />
  </svg>
);

const svg_expand = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path fillRule="evenodd" clipRule="evenodd" d="M17.8107 10.5607L16.75 9.5L12.1544 14.0956L7.56066 9.50184L6.5 10.5625L11.0937 15.1562L11.0931 15.1569L12.1538 16.2175L12.1544 16.2169L12.1569 16.2194L13.2175 15.1587L13.2151 15.1563L17.8107 10.5607Z" fill="#222222" />
  </svg>
);

const moneyInput = {
  controls: false,
  prefix: "$",
  formatter: (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","),
  parser: (value) => String(value).replace(/\$\s?|(,*)/g, ""),
};

// The filter bar above the results (plan section 6.0, D26) and the requests
// behind it: one per ticked loan term. Typing a target price or rate only
// changes which rate each product shows; switching Price / Rate, a new
// property value, down payment, LTV or compensation type re-quotes (on Enter
// or leaving the box); ticking a term that was not priced prices just that
// term.
const SearchResults = ({
  searchParams,
  customerid,
  setChangeShowResults,
  onRefineSearch,
  onApplyAlternative,
  style,
  searchSource = "check_rates",
}) => {
  const sp = searchParams || {};
  const isPurchase = sp.purpose === "Purchase";
  const secondLien = SECOND_LIEN_PROGRAMS.includes(sp.loanProgram);
  const { quotes, searchKey, body: quotedBody, quote, addTerms } = usePpeQuotes();

  const [targetBy, setTargetBy] = useState("price");
  const [targetPrice, setTargetPrice] = useState(100);
  const [targetRate, setTargetRate] = useState(null);
  // What the target price is matched against: Price or Cost to borrower.
  const [targetBasis, setTargetBasis] = useState(TARGET_BASIS_PRICE);
  const [compType, setCompType] = useState(sp.compensationType);
  const [propertyValue, setPropertyValue] = useState(0);
  const [downPayment, setDownPayment] = useState(0);
  const [loanAmount, setLoanAmount] = useState(0);
  const [ltv, setLtv] = useState(undefined);
  const [terms, setTerms] = useState(["fixed30"]);
  const [lenderChoice, setLenderChoice] = useState({ on: [], off: [] });
  // Folded term groups (11-04): kept here, not in the table, so a re-quote
  // (which shows the loading table for a moment) keeps them; a new search
  // opens every group.
  const [foldedTerms, setFoldedTerms] = useState(() => new Set());
  const [openLender, setOpenLender] = useState(false);
  const quotedValues = useRef({});
  const searchSourceLatest = useRef(searchSource);
  searchSourceLatest.current = searchSource;

  const ltvFrom = (value, down, amount) => {
    const pv = Number(value) || 0;
    if (pv <= 0) return undefined;
    const subject = isPurchase && !secondLien ? pv - (Number(down) || 0) : Number(amount) || 0;
    return round2(subject / pv * 100);
  };

  // A new search from the form (or a saved search): reset the bar, price every ticked term.
  useEffect(() => {
    if (!searchParams || !searchParams.propertyValue) return;
    const startTerms = termsOfSearch(searchParams);
    const startTarget = initialBarTarget(searchParams);
    const values = {
      propertyValue: numberOr(searchParams.propertyValue, 0),
      downPayment: isPurchase ? numberOr(searchParams.downPayment, 0) : undefined,
      loanAmount: numberOr(searchParams.loanAmount, 0),
      compType: searchParams.compensationType,
      searchBy: startTarget.by,
      targetPrice: startTarget.price,
      targetRate: startTarget.rate,
      targetPriceBasis: startTarget.basis,
    };
    setTargetBy(startTarget.by);
    setTargetPrice(startTarget.price);
    setTargetRate(startTarget.rate);
    setTargetBasis(startTarget.basis);
    setCompType(values.compType);
    setPropertyValue(values.propertyValue);
    setDownPayment(values.downPayment);
    setLoanAmount(values.loanAmount);
    setLtv(ltvFrom(values.propertyValue, values.downPayment, values.loanAmount));
    setTerms(startTerms);
    setLenderChoice({ on: [], off: [] });
    setFoldedTerms(new Set());
    quotedValues.current = values;
    quote(searchParams, startTerms);
    track(STATSIG.RATES.SEARCH, loanAmountOf(searchParams), { source: searchSourceLatest.current || "check_rates" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams && searchParams._timestamp]);

  const filteredBody = (values) => filterBarBody(sp, values);

  const requote = (values) => {
    const next = { ...quotedValues.current, ...values };
    const same = ["propertyValue", "downPayment", "loanAmount", "compType", "searchBy", "targetPriceBasis"].every((k) => quotedValues.current[k] === next[k]);
    if (same) return;
    if (!(Number(next.propertyValue) > 0)) return;
    quotedValues.current = next;
    quote(filteredBody(next), terms);
  };

  // The target typed so far goes with any re-quote, so the saved search and
  // the calculator's order match what the table shows.
  const targetValues = () => ({ searchBy: targetBy, targetPrice, targetRate, targetPriceBasis: targetBasis });
  const commitValues = () => requote({ propertyValue, downPayment, loanAmount, ...targetValues() });

  // --- derived results -------------------------------------------------------
  const body = quotedBody || sp;
  const ctx = useMemo(() => ({
    loanAmount: loanAmountOf(body),
    lockDays: body.lockDay,
    compensationType: body.compensationType,
    customerid: customerid || com.getCustomerId(),
  }), [body, customerid]);
  const products = useMemo(() => buildProducts(quotes, ctx), [quotes, ctx]);
  const scenario = { loanProgram: body.loanProgram, lienPosition: SECOND_LIEN_PROGRAMS.includes(body.loanProgram) || body.lienPosition === "second" ? "second" : "first" };
  const ineligible = useMemo(() => mergeIneligible(quotes, scenario), [quotes, scenario.loanProgram, scenario.lienPosition]); // eslint-disable-line react-hooks/exhaustive-deps
  const connectedMap = useMemo(() => {
    const out = {};
    Object.values(quotes).forEach((q) => Object.entries((q.response && q.response.connected_lender_map) || {}).forEach(([k, v]) => { out[k] = out[k] || v; }));
    return out;
  }, [quotes]);
  const noLendersAdded = Object.keys(connectedMap).length > 0 && Object.values(connectedMap).every((v) => !v);

  const options = useMemo(() => lenderOptions(products), [products]);
  const selectedLenders = options
    .filter((o) => !lenderChoice.off.includes(o.value) && (lenderChoice.on.includes(o.value) || noLendersAdded || o.myLenders))
    .map((o) => o.value);
  const termCounts = countBy(visibleProducts(products, { lenders: selectedLenders }), "term");
  const lenderCounts = countBy(visibleProducts(products, { terms }), "lender");

  const termStates = Object.values(quotes);
  const connectedPriced = products.some((p) => p.isConnected && terms.includes(p.term));
  const { loading: initialLoading, busy, settled } = quotePhase(termStates, connectedPriced, Boolean(sp.propertyValue));
  const allFailed = termStates.length > 0 && termStates.every((q) => q.status === "error");
  const firstError = termStates.find((q) => q.status === "error");
  const appliedClosingCostsDetail = (termStates.find((q) => q.response && q.response.appliedClosingCostsDetail) || {}).response?.appliedClosingCostsDetail;
  // "Nothing qualifies" only once every term has answered: a term that fails
  // fast or comes back empty must not read as "no qualified products" while
  // the others are still pricing.
  const zeroText = !connectedPriced && settled ? zeroSentence(quotes, ineligible, scenario) : "";

  // Change suggestions (D18, work package 8): only when nothing is eligible,
  // once per search and set of ticked terms, after every term is priced.
  const [alternatives, setAlternatives] = useState({ key: null, status: "idle", list: [] });
  const alternativesKey = `${searchKey}|${terms.join(",")}`;
  const wantAlternatives = Boolean(zeroText) && !busy && termStates.length > 0 && !allFailed;
  useEffect(() => {
    if (!wantAlternatives || alternatives.key === alternativesKey) return;
    const mine = alternativesKey;
    setAlternatives({ key: mine, status: "loading", list: [] });
    fetchAlternatives(quotedBody || sp, terms).then(
      (response) => setAlternatives((prev) => (prev.key === mine ? { key: mine, status: "done", list: shownAlternatives(response) } : prev)),
      () => setAlternatives((prev) => (prev.key === mine ? { key: mine, status: "error", list: [] } : prev)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantAlternatives, alternativesKey]);
  const shownSuggestions = wantAlternatives && alternatives.key === alternativesKey ? alternatives : { status: "idle", list: [] };
  const tryAlternative = (alt) => {
    if (!onApplyAlternative) return;
    onApplyAlternative(applyAlternative(quotedBody || sp, alt, terms));
  };

  // --- filter actions ----------------------------------------------------------
  const changeTerms = (next) => {
    if (!next || next.length === 0) return; // at least one term
    setTerms(next);
    const unpriced = next.filter((t) => !quotes[t] || quotes[t].status === "error");
    if (unpriced.length > 0) addTerms(quotedBody || sp, unpriced);
  };

  const setLender = (value, checked) => setLenderChoice((prev) => ({
    on: checked ? [...new Set([...prev.on, value])] : prev.on.filter((v) => v !== value),
    off: checked ? prev.off.filter((v) => v !== value) : [...new Set([...prev.off, value])],
  }));
  const selectAll = (mine) => {
    const group = options.filter((o) => o.myLenders === mine).map((o) => o.value);
    const allOn = group.every((v) => selectedLenders.includes(v));
    group.forEach((v) => setLender(v, !allOn));
  };
  const clearAll = () => setLenderChoice({ on: [], off: options.map((o) => o.value) });

  const handleRefineSearch = () => {
    const values = { ...quotedValues.current, propertyValue, downPayment, loanAmount, compType, ...targetValues() };
    onRefineSearch({
      ...filteredBody(values),
      selectedLoanTerms: terms,
      loanTerm: terms[0],
      lenders: selectedLenders,
    });
    setChangeShowResults();
  };

  // Loan terms: the same multi-select as the form, grouped Fixed / ARM; each
  // option says how many products it has, or that it is not priced yet.
  const termCountText = (t) => {
    const q = quotes[t];
    if (q && q.status === "done") return String(termCounts[t] || 0);
    if (q && q.status === "loading") return "pricing…";
    return "not priced";
  };
  const termOptions = termGroupsFor(sp.loanProgram).map(([group, list]) => ({
    label: group,
    title: group,
    options: list.map((t) => ({
      value: t,
      title: termLabel(t),
      label: (
        <span className="ppe-term-option">
          <span>{termLabel(t)}</span>
          <span className="ppe-term-count">{termCountText(t)}</span>
        </span>
      ),
    })),
  }));

  let lenderSummary = "Select lenders";
  if (selectedLenders.length > 0 && selectedLenders.length === options.length) lenderSummary = "All lenders";
  else if (selectedLenders.length > 0) {
    const first = (options.find((o) => o.value === selectedLenders[0]) || {}).label;
    lenderSummary = selectedLenders.length > 1 ? `${first} +${selectedLenders.length - 1}` : first;
  }

  const lenderDropdownContent = () => (
    <div className="document-lender-content" style={{ width: 410 }}>
      <div className="lender-title">
        <span>Lenders</span>
        <div className="close-lender-box">
          <CloseOutlined className="close-lender" onClick={() => setOpenLender(false)} aria-label="Close" />
        </div>
      </div>
      <div className="lender-select" style={{ maxHeight: window.innerHeight >= 768 ? 560 : 380, overflowY: "auto" }}>
        {[true, false].map((mine) => {
          const list = options.filter((o) => o.myLenders === mine);
          if (list.length === 0) return null;
          return (
            <div key={String(mine)} style={{ width: "100%", marginTop: mine ? 0 : 32 }}>
              <div className="lender-select-item-title">
                <div className="select-title">
                  <span>{mine ? "My lenders" : "Products from other lenders"}</span>
                  {!mine && (
                    <Tooltip placement="top" title="Explore competitive rates from Zeitro's partner lenders. Connect directly to learn more and get started.">
                      <QuestionCircleOutlined style={{ fontSize: 14, cursor: "pointer", marginLeft: 6 }} />
                    </Tooltip>
                  )}
                </div>
                <button type="button" className="select-all ppe-plain" onClick={() => selectAll(mine)}>Select all</button>
              </div>
              {list.map((item) => (
                <div style={{ height: 42, display: "flex", alignItems: "center" }} key={item.value}>
                  <Checkbox
                    className="lender-checkbox"
                    checked={selectedLenders.includes(item.value)}
                    onChange={(e) => setLender(item.value, e.target.checked)}
                  >
                    <span className="lender-checkbox-label">{item.label}</span>
                    <span className="ppe-lender-count">{lenderCounts[item.value] || 0}</span>
                  </Checkbox>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      <div className="lender-action">
        <button type="button" className="lender-clear ppe-plain" onClick={clearAll}>Clear</button>
      </div>
    </div>
  );

  // Price | Rate (11-04). Switching re-quotes by the new target; a Rate box
  // left empty starts at the lowest rate the table shows at the target price.
  const byRate = targetBy === "rate";
  // One object per value, so the table's "new target" effect does not run on
  // every render.
  const tableTarget = useMemo(() => (byRate && Number(targetRate) > 0
    ? { by: "rate", value: Number(targetRate) }
    : { by: "price", value: targetPrice, basis: targetBasis }), [byRate, targetRate, targetPrice, targetBasis]);
  const switchTarget = (value) => {
    if (value === targetBy) return;
    let rate = targetRate;
    if (value === "rate" && !(Number(rate) > 0)) {
      rate = startingTargetRate(visibleProducts(products, { terms, lenders: selectedLenders }), targetPrice, targetBasis);
      setTargetRate(rate);
    }
    setTargetBy(value);
    if (value === "rate" && !(Number(rate) > 0)) return; // nothing to search by yet
    requote({ propertyValue, downPayment, loanAmount, compType, searchBy: value, targetPrice, targetRate: rate, targetPriceBasis: targetBasis });
  };
  // Price or Cost to borrower (11-04): the table moves its Target rows at
  // once, and the re-quote saves the choice with the search.
  const switchBasis = (value) => {
    if (value === targetBasis) return;
    setTargetBasis(value);
    requote({ propertyValue, downPayment, loanAmount, compType, ...targetValues(), targetPriceBasis: value });
  };
  // The loan amount the "≈ $4,000 cost to borrower" line uses: what the bar
  // shows now, before any re-quote.
  const hintLoanAmount = barLoanAmount(sp, { propertyValue, downPayment, loanAmount });

  const onEnter = (e) => {
    if (e.key === "Enter") commitValues();
  };

  return (
    <ConfigProvider>
      <div className="search-results" style={style}>
        <div className="ppe-bar">
          <div className="ppe-bar-title">
            <button type="button" className="ppe-plain ppe-bar-heading" aria-label="Open the search form" onClick={handleRefineSearch}>
              <span>Search fields</span>
              {svg_expand()}
            </button>
            {busy && <span className="ppe-busy" role="status"><Spin size="small" /> Updating…</span>}
            <button type="button" className="ppe-bar-refine" onClick={handleRefineSearch}>Refine search</button>
          </div>
          <div className={`ppe-bar-fields ${secondLien ? "ppe-bar-second-lien" : ""}`}>
            <div className="ppe-bar-field ppe-bar-target">
              <div className="ppe-bar-label-row">
                <label className="ppe-bar-label" htmlFor="ppe-filter-target">{byRate ? "Target rate" : "Target price"}</label>
                {!byRate && <TargetBasisPicker id="ppe-filter-target-basis" value={targetBasis} onChange={switchBasis} short />}
              </div>
              <div className="ppe-pair">
                <Segmented
                  aria-label="Target by"
                  className="ppe-bar-segmented ppe-bar-target-by"
                  options={[
                    { label: "Price", value: "price" },
                    { label: "Rate", value: "rate" },
                  ]}
                  value={targetBy}
                  onChange={switchTarget}
                />
                {byRate ? (
                  <InputNumber
                    key="rate"
                    id="ppe-filter-target"
                    className="ppe-bar-input ppe-target-input"
                    controls={false}
                    step={0.125}
                    precision={3}
                    min={0}
                    max={20}
                    suffix="%"
                    value={targetRate}
                    onChange={(value) => setTargetRate(value === null || value === undefined ? null : Number(value))}
                    onBlur={commitValues}
                    onKeyDown={onEnter}
                  />
                ) : (
                  <InputNumber
                    key="price"
                    id="ppe-filter-target"
                    className="ppe-bar-input ppe-target-input"
                    controls={false}
                    step={0.125}
                    precision={3}
                    min={80}
                    max={120}
                    value={targetPrice}
                    onChange={(value) => setTargetPrice(numberOr(value, 100))}
                    onBlur={commitValues}
                    onKeyDown={onEnter}
                    aria-describedby={!byRate && targetBasis === TARGET_BASIS_COST ? "ppe-filter-target-hint" : undefined}
                  />
                )}
              </div>
              {!byRate && <TargetBasisHint id="ppe-filter-target-hint" basis={targetBasis} price={targetPrice} loanAmount={hintLoanAmount} />}
            </div>
            <div className="ppe-bar-field ppe-bar-comp">
              <span className="ppe-bar-label" id="ppe-filter-comp-label">Compensation</span>
              <Segmented
                block
                aria-labelledby="ppe-filter-comp-label"
                className="ppe-bar-segmented"
                options={[
                  { label: "Lender paid", value: "lender_paid" },
                  { label: "Borrower paid", value: "borrower_paid" },
                ]}
                value={compType}
                onChange={(value) => {
                  setCompType(value);
                  requote({ compType: value, propertyValue, downPayment, loanAmount, ...targetValues() });
                }}
              />
            </div>
            <div className="ppe-bar-field ppe-bar-value">
              <label className="ppe-bar-label" htmlFor="ppe-filter-value">{isPurchase ? "Property value" : "Home value"}</label>
              <InputNumber
                {...moneyInput}
                id="ppe-filter-value"
                className="ppe-bar-input"
                value={propertyValue}
                onChange={(value) => {
                  setPropertyValue(value);
                  setLtv(ltvFrom(value, downPayment, loanAmount));
                }}
                onBlur={commitValues}
                onKeyDown={onEnter}
              />
            </div>
            {!secondLien && (
              <div className="ppe-bar-field ppe-bar-pair">
                <label className="ppe-bar-label" htmlFor="ppe-filter-amount">{isPurchase ? "Down payment / LTV" : "Loan amount / LTV"}</label>
                <div className="ppe-pair">
                  <InputNumber
                    {...moneyInput}
                    id="ppe-filter-amount"
                    aria-label={isPurchase ? "Down payment" : "Loan amount"}
                    className="ppe-bar-input"
                    value={isPurchase ? downPayment : loanAmount}
                    onChange={(value) => {
                      if (isPurchase) {
                        setDownPayment(value);
                        setLoanAmount((Number(propertyValue) || 0) - (Number(value) || 0));
                        setLtv(ltvFrom(propertyValue, value, loanAmount));
                      } else {
                        setLoanAmount(value);
                        setLtv(ltvFrom(propertyValue, downPayment, value));
                      }
                    }}
                    onBlur={commitValues}
                    onKeyDown={onEnter}
                  />
                  <InputNumber
                    aria-label="LTV"
                    className="ppe-bar-input ppe-bar-ltv"
                    controls={false}
                    suffix="%"
                    min={0}
                    max={100}
                    precision={2}
                    value={ltv}
                    onChange={(value) => {
                      setLtv(value);
                      const pv = Number(propertyValue) || 0;
                      if (value === null || value === undefined || pv <= 0) return;
                      const subject = Math.round(pv * Number(value) / 100);
                      if (isPurchase) {
                        setDownPayment(pv - subject);
                        setLoanAmount(subject);
                      } else {
                        setLoanAmount(subject);
                      }
                    }}
                    onBlur={commitValues}
                    onKeyDown={onEnter}
                  />
                </div>
              </div>
            )}
            <div className="ppe-bar-field ppe-bar-terms">
              <span className="ppe-bar-label" id="ppe-filter-terms-label">Loan terms</span>
              <Select
                id="ppe-filter-terms"
                aria-labelledby="ppe-filter-terms-label"
                className="ppe-bar-terms-select"
                mode="multiple"
                suffixIcon={suffix_svg()}
                value={terms}
                options={termOptions}
                optionLabelProp="title"
                maxTagCount="responsive"
                onChange={changeTerms}
                showSearch={false}
                popupClassName="ppe-terms-popup"
              />
            </div>
            <div className="ppe-bar-field ppe-bar-lenders">
              <span className="ppe-bar-label" id="ppe-filter-lenders-label">Lenders</span>
              <Dropdown
                trigger={["click"]}
                overlayClassName="filter-dropdown"
                open={openLender}
                dropdownRender={lenderDropdownContent}
                placement="bottomRight"
                onOpenChange={setOpenLender}
              >
                <button type="button" className="ppe-bar-select" aria-labelledby="ppe-filter-lenders-label" aria-haspopup="dialog" aria-expanded={openLender}>
                  <span className="ppe-bar-select-text">{lenderSummary}</span>
                  {suffix_svg()}
                </button>
              </Dropdown>
            </div>
          </div>
        </div>
        <RateQuoteNewResultTable
          loading={initialLoading}
          error={allFailed && firstError ? { title: firstError.title, message: firstError.error } : null}
          noLendersAdded={noLendersAdded}
          searchKey={searchKey}
          searchId={searchParams && searchParams._timestamp}
          foldedTerms={foldedTerms}
          onToggleGroup={(term) => setFoldedTerms((prev) => toggledSet(prev, term))}
          products={products}
          quotes={quotes}
          terms={terms}
          lenders={selectedLenders}
          target={tableTarget}
          ineligible={ineligible}
          zeroText={zeroText}
          alternatives={shownSuggestions}
          onTryAlternative={onApplyAlternative ? tryAlternative : undefined}
          hasIncome={incomeEntered(body)}
          loanAmount={ctx.loanAmount}
          lockDays={body.lockDay}
          compensationType={body.compensationType}
          appliedClosingCostsDetail={appliedClosingCostsDetail}
          downPayment={body.downPayment}
          purpose={body.purpose}
          searchParams={body}
          statsigSource="rates"
        />
      </div>
    </ConfigProvider>
  );
};

export default SearchResults;
